/**
 * Replaces Shopify's filter counts on collection pages with the number of
 * product cards the grid actually renders. Shopify counts products and
 * includes out-of-stock variants, so a size filter can say (20) while only 4
 * cards are shown.
 *
 * Mirrors the card logic in sections/main-collection.liquid: a card is a
 * product + option1 value (color) with at least one available variant that
 * matches every active variant-option filter. Product-level filters (type,
 * vendor, tags, metafields) and price narrow down the products first.
 *
 * Data comes from templates/collection.stock-counts.liquid. If anything can't
 * be matched reliably, Shopify's server-rendered counts are left untouched.
 */
(() => {
  const KIND_OPTION = 'option';
  const KIND_PRODUCT = 'product';
  const KIND_PRICE = 'price';
  const SUPPORTED_KINDS = [KIND_OPTION, KIND_PRODUCT, KIND_PRICE];
  const PRICE_MIN_PARAM = 'filter.v.price.gte';
  const PRICE_MAX_PARAM = 'filter.v.price.lte';
  const CENTS_PER_UNIT = 100;
  const UPDATED_EVENT = 'on:facet-filters:updated';
  const LOG_PREFIX = '[stock-filter-counts]';

  /**
   * Normalizes a product's raw filter value(s) to a set of URL filter values.
   * @param {*} raw - String, array, boolean or null from the stock view.
   * @returns {Set<string>}
   */
  function toValueSet(raw) {
    const values = new Set();
    if (raw === null || raw === undefined) return values;

    (Array.isArray(raw) ? raw : [raw]).forEach((item) => {
      if (typeof item === 'boolean') {
        // Boolean filters use '1'/'0' in the URL; keep the literal too in case
        // a store's filter exposes 'true'/'false'.
        values.add(item ? '1' : '0');
        values.add(String(item));
      } else {
        values.add(String(item));
      }
    });
    return values;
  }

  /**
   * Validates and prepares the stock view payload.
   * @param {object} data - Parsed JSON from the stock view.
   * @returns {{groups: Map<string, string>, products: Array<object>}}
   * @throws {Error} When the data is incomplete or can't be matched reliably.
   */
  function parseStockData(data) {
    if (!data || data.complete !== true) {
      throw new Error('collection too large or stock data incomplete');
    }
    if (!data.groups || !Array.isArray(data.products)) {
      throw new Error('malformed stock data');
    }

    const groups = new Map(Object.entries(data.groups));
    const unsupported = [...groups].filter(([, kind]) => !SUPPORTED_KINDS.includes(kind));
    if (unsupported.length > 0) {
      throw new Error(`unsupported filters: ${unsupported.map(([name]) => name).join(', ')}`);
    }

    const products = data.products.map((raw) => {
      const props = new Map();
      Object.entries(raw.p || {}).forEach(([name, value]) => {
        if (value === null) throw new Error(`unsupported metafield type for ${name}`);
        props.set(name, toValueSet(value));
      });

      return {
        props,
        prices: Array.isArray(raw.r) ? raw.r : [],
        variants: Array.isArray(raw.v) ? raw.v : []
      };
    });

    return { groups, products };
  }

  /**
   * @param {Set<string>} a
   * @param {Set<string>} b
   * @returns {boolean}
   */
  function intersects(a, b) {
    for (const value of b) {
      if (a.has(value)) return true;
    }
    return false;
  }

  /**
   * @param {object} product - Parsed product.
   * @param {object} selection - Active filters.
   * @returns {boolean}
   */
  function matchesProductFilters(product, selection) {
    for (const [name, group] of selection.groups) {
      if (group.kind === KIND_PRODUCT && group.values.size > 0
        && !intersects(product.props.get(name) || new Set(), group.values)) {
        return false;
      }
    }

    const { min, max } = selection.price;
    if (min === null && max === null) return true;

    const minCents = min === null ? -Infinity : Math.round(min * CENTS_PER_UNIT);
    const maxCents = max === null ? Infinity : Math.round(max * CENTS_PER_UNIT);
    return product.prices.some((price) => price >= minCents && price <= maxCents);
  }

  /**
   * Checked against all of the variant's options, like the grid does, since a
   * filter can't be reliably mapped to one option position.
   * @param {Array<string>} options - The variant's option values.
   * @param {object} selection - Active filters.
   * @returns {boolean}
   */
  function matchesOptionFilters(options, selection) {
    for (const group of selection.groups.values()) {
      if (group.kind === KIND_OPTION && group.values.size > 0
        && !options.some((option) => group.values.has(option))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Counts the cards the grid would render for a selection.
   * @param {Array<object>} products - Parsed products.
   * @param {object} selection - Active filters.
   * @returns {number}
   */
  function countCards(products, selection) {
    let total = 0;

    products.forEach((product) => {
      if (!matchesProductFilters(product, selection)) return;

      const colors = new Set();
      product.variants.forEach((options) => {
        if (matchesOptionFilters(options, selection)) colors.add(String(options[0] ?? '').trim());
      });
      total += colors.size;
    });

    return total;
  }

  /**
   * Returns a copy of the selection with one group narrowed to a single value,
   * i.e. what a filter value's count represents (values within a group are OR'd,
   * so the group's other active values don't apply).
   * @param {object} selection - Active filters.
   * @param {string} name - Filter param name.
   * @param {string} value - Filter value.
   * @returns {object}
   */
  function withGroupValue(selection, name, value) {
    const groups = new Map(selection.groups);
    groups.set(name, { ...selection.groups.get(name), values: new Set([value]) });
    return { ...selection, groups };
  }

  /**
   * Fills a server-rendered count string with a new count.
   * @param {{one: string, other: string}} templates - Strings rendered for count 1 and 2.
   * @param {number} count - New count.
   * @param {string} locale - Page locale.
   * @returns {string}
   */
  function formatCount(templates, count, locale) {
    let category = count === 1 ? 'one' : 'other';
    try {
      category = new Intl.PluralRules(locale || undefined).select(count) === 'one' ? 'one' : 'other';
    } catch (error) {
      // Invalid locale tag; the count === 1 fallback above is fine.
    }
    return templates[category].replace(/\d+/, String(count));
  }

  /**
   * Reads the active filters from the filter form.
   * @param {HTMLFormElement} form - Filter form.
   * @param {Map<string, string>} groups - Filter kinds by param name.
   * @returns {object}
   * @throws {Error} When the form has a filter the stock data doesn't know about.
   */
  function readSelection(form, groups) {
    const selection = { groups: new Map(), price: { min: null, max: null } };

    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      const kind = groups.get(input.name);
      if (!kind) throw new Error(`unknown filter ${input.name}`);

      if (!selection.groups.has(input.name)) {
        selection.groups.set(input.name, { kind, values: new Set() });
      }
      if (input.checked) selection.groups.get(input.name).values.add(input.value);
    });

    const parsePrice = (name) => {
      const input = form.querySelector(`input[name="${name}"]`);
      const value = input ? parseFloat(input.value) : NaN;
      return Number.isFinite(value) ? value : null;
    };
    selection.price = { min: parsePrice(PRICE_MIN_PARAM), max: parsePrice(PRICE_MAX_PARAM) };

    return selection;
  }

  /**
   * Writes the in-stock counts into the filters, results header and drawer button.
   * @param {HTMLElement} el - The facet-filters element.
   * @param {object} stock - Parsed stock data.
   */
  function render(el, stock) {
    const form = el.querySelector('#facets');
    if (!form) return;

    const selection = readSelection(form, stock.groups);
    const locale = document.documentElement.lang;

    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      const count = countCards(stock.products, withGroupValue(selection, input.name, input.value));
      const countEl = input.parentElement.querySelector('.js-filter-count');

      if (countEl) countEl.textContent = `(${count})`;
      input.disabled = count === 0 && !input.checked;
    });

    const total = countCards(stock.products, selection);
    const resultsCountEl = document.getElementById('results-count');
    const footerBtn = el.querySelector('.facets__footer .js-close-drawer');

    if (resultsCountEl) {
      resultsCountEl.textContent = formatCount(
        { one: el.dataset.productCountOne, other: el.dataset.productCountOther }, total, locale
      );
    }
    if (footerBtn) {
      footerBtn.textContent = formatCount(
        { one: el.dataset.resultsCountOne, other: el.dataset.resultsCountOther }, total, locale
      );
    }
  }

  function init() {
    const el = document.querySelector('facet-filters[data-stock-counts-url]');
    const url = el ? el.dataset.stockCountsUrl : '';
    if (!url) return;

    const stockPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`stock view returned HTTP ${response.status}`);
        return response.json();
      })
      .then(parseStockData);

    const update = () => stockPromise
      .then((stock) => render(el, stock))
      .catch((error) => {
        console.warn(`${LOG_PREFIX} Falling back to Shopify's filter counts:`, error);
      });

    // facet-filters.js swaps in server-rendered counts after every filter
    // change, so re-apply ours each time.
    el.addEventListener(UPDATED_EVENT, update);
    update();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseStockData, countCards, withGroupValue, formatCount, toValueSet };
    return;
  }

  init();
})();
